import { client, log } from ".";
import { capturarSnapshotComision, capturarSnapshotDesdeVenta } from "./comisionSnapshot";
import { getClientes } from "./getClientes";
import { getIntermediarios } from "./getIntermediarios";
import { resolverPctsCongelamiento } from "./resolverPctsCongelamiento";

const parseIds = (csv) =>
  (csv || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

// Localiza el padre del snapshot (sale o contrato-mes) y devuelve { id, pct_congelado_en } o null.
const fetchPadre = async (origenTipo, saleId, contratoId, mes) => {
  let q = client.from("comision_snapshot").select("id, pct_congelado_en");
  q =
    origenTipo === "sale"
      ? q.eq("origen_tipo", "sale").eq("sale_id", saleId)
      : q.eq("origen_tipo", "contrato_mes").eq("contrato_id", contratoId).eq("mes", mes);
  const { data } = await q;
  return data?.[0] || null;
};

/**
 * Congela el % de intermediación en el padre `comision_snapshot` al facturar (Fase A2b, ver 7.22).
 * Aditivo: NO cambia ningún número mostrado (los reads de la foto son A3). No lanza — si falla, loguea
 * y devuelve un resultado; nunca aborta la facturación que lo invoca.
 *
 * - **Idempotente:** si el padre ya tiene `pct_congelado_en` NOT NULL → no-op (la re-facturación tras
 *   desfacturar la limpia en A4).
 * - **Ensure padre:** si no existe la fila padre, la captura con el mecanismo de comisionSnapshot
 *   (denominador/importe actuales). Para contrato-mes captura SÓLO ese mes (no `capturarSnapshotDesde-
 *   Contrato`, que re-captura todos y pisaría el freeze de otros meses ya congelados).
 * - Resuelve los 4 % con `resolverPctsCongelamiento` y hace un UPDATE atómico de las 6 columnas
 *   (respeta `chk_pct_congelado_coherencia`). Auto-carga clientes/intermediarios si no se pasan
 *   (one-shot al facturar). Resolución por id (regla 10.7).
 */
export const congelarPctIntermediacion = async ({
  origenTipo,
  saleId = null,
  contratoId = null,
  mes = null,
  facturaDate = null,
  clientes = null,
  intermediarios = null,
}) => {
  try {
    // 1. Idempotencia
    let padre = await fetchPadre(origenTipo, saleId, contratoId, mes);
    if (padre?.pct_congelado_en != null) return { ok: true, accion: "ya-congelado" };

    // 2. Fila autoritativa + ensure padre + armar `venta` para resolver %
    let venta;
    let paisVenta;
    let fechaBase;
    if (origenTipo === "sale") {
      const { data: s } = await client.from("sales").select("*").eq("id", saleId).single();
      if (!s) return { ok: false, accion: "sin-venta" };
      venta = s;
      paisVenta = s.pais;
      fechaBase = facturaDate || s.facturafecha;
      if (!padre) await capturarSnapshotDesdeVenta(s, "modificacion");
    } else {
      const { data: c } = await client.from("sales_contratos").select("*").eq("id", contratoId).single();
      if (!c) return { ok: false, accion: "sin-contrato" };
      const cuota = (c.meses || []).find((m) => m.mes === mes);
      venta = {
        client_id: c.client_id,
        pais: c.pais,
        apx_id: c.apx_id,
        descuento_acuerdo_pct: c.descuento_acuerdo_pct,
        facturafecha: facturaDate || cuota?.facturafecha || null,
      };
      paisVenta = c.pais;
      fechaBase = facturaDate || cuota?.facturafecha;
      if (!padre) {
        // Capturar SÓLO este mes (aislado): no pisa otros meses ya congelados del contrato.
        const ids = parseIds(cuota?.edificios_ids);
        if (ids.length > 0) {
          const { data: edificios } = await client
            .from("edificios")
            .select("id, pantallas, totems, espera, cowork")
            .in("id", ids);
          await capturarSnapshotComision({
            origen_tipo: "contrato_mes",
            contrato_id: contratoId,
            mes,
            edificios: edificios || [],
            csvs: {
              pantallas_ascensores: cuota?.pantallas_ascensores,
              pantallas_circulacion: cuota?.pantallas_circulacion,
              pantallas_espera: cuota?.pantallas_espera,
              pantallas_cowork: cuota?.pantallas_cowork,
            },
            monto: cuota?.importe ?? null,
            pais: c.pais,
            motivo: "modificacion",
          });
        }
      }
    }

    // Re-fetch el padre si lo acabamos de crear
    if (!padre) padre = await fetchPadre(origenTipo, saleId, contratoId, mes);
    if (!padre) return { ok: false, accion: "sin-padre" }; // venta sin edificios → no hay foto que congelar

    // 3. Resolver los 4 % efectivos al facturar
    const cli = clientes || (await getClientes({ pais: paisVenta }).catch(() => []));
    const inter = intermediarios || (await getIntermediarios({ pais: paisVenta }).catch(() => []));
    const pcts = resolverPctsCongelamiento(venta, cli, inter, fechaBase ? new Date(fechaBase) : new Date());

    // 4. UPDATE atómico de las 6 columnas (satisface chk_pct_congelado_coherencia)
    const { error } = await client
      .from("comision_snapshot")
      .update({
        pct_agencia_congelado: pcts.agencia,
        pct_apx_congelado: pcts.apx,
        pct_broker_congelado: pcts.broker,
        pct_descuento_congelado: pcts.descuento,
        pct_congelado_en: new Date().toISOString(),
        pct_congelado_motivo: "facturacion",
      })
      .eq("id", padre.id);
    if (error) throw error;
    return { ok: true, accion: "congelado", pcts };
  } catch (e) {
    log.error("congelarPctIntermediacion", e);
    return { ok: false, accion: "error", error: String(e?.message || e) };
  }
};
